import { CompanyTag } from "@/components/domain/CompanyTag";
import { type Paginated, type PiSummaryDto } from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { apiFetch } from "@/lib/api";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Stack } from "@/components/ui/Stack";
import {
  EmptyTableRow,
  ListTableFrame,
  LIST_PAGE_BODY,
} from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { PaymentStatusChip } from "@/components/domain/StatusChip";
import { useCustomers, useProformaInvoices } from "@/api/hooks";
import { formatMoney, majorUnits } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import type { PiPaymentStatus } from "@/domain/types";
import { translateStatus } from "@/domain/i18n";
import { useClampListPage, useListQueryState, useParamInput } from "@/lib/listQuery";
import { ExportButton } from "@/components/domain/ExportButton";
import { RowPrintButton } from "@/components/domain/RowPrintButton";
import { printProformaInvoice } from "@/lib/printPi";

const STATUS_FILTERS: Array<PiPaymentStatus | "all"> = [
  "all",
  "unpaid",
  "partial",
  "paid",
];

export function ProformaInvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useListQueryState();
  const respByInput = useParamInput(list, "respBy");
  const { user } = useAuth();
  const can = useCan();
  const customers = useCustomers({});
  const context = list.searchParams.size ? `?${list.searchParams}` : "";
  const full = list.searchParams.get("view") === "full";
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const filters = {
    customerId: list.searchParams.get("customerId") || undefined,
    respBy: list.searchParams.get("respBy") || undefined,
    from: from ? new Date(from + "T00:00:00Z").getTime() : undefined,
    to: to ? new Date(to + "T23:59:59Z").getTime() : undefined,
    sort: list.searchParams.get("sort") || undefined,
  };
  const statusParam = list.searchParams.get("status");
  const status: PiPaymentStatus | "all" = STATUS_FILTERS.includes(
    statusParam as PiPaymentStatus | "all",
  )
    ? (statusParam as PiPaymentStatus | "all")
    : "all";
  const { data, isLoading, isFetching, error, refetch } = useProformaInvoices({
    ...filters,
    q: list.q.trim() || undefined,
    status,
    page: list.page,
    pageSize: list.pageSize,
  });
  useClampListPage(data?.total, list.clampPage);

  const rows = data?.data ?? [];

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...list.searchInput}
          leading={<Search />}
          placeholder={t("common.searchPlaceholder")}
          className="w-full max-w-xs"
        />
        <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => list.updateParam("status", f === "all" ? null : f)}
              className={
                "rounded-[3px] px-2.5 py-1 text-xs font-medium capitalize transition-colors " +
                (status === f
                  ? "bg-brand text-brand-on"
                  : "text-ink-faint hover:text-ink-soft")
              }
            >
              {f === "all" ? t("common.all") : translateStatus(t, f)}
            </button>
          ))}
        </div>
        {/*
          MỘT bộ cột cho cả hai phạm vi. Trước đây hai nút cạnh nhau cho ra hai
          tệp khác cấu trúc: "trang này" có Ngày và Trạng thái nhưng thiếu tiền,
          "toàn bộ đã lọc" có tiền nhưng thiếu Ngày và Trạng thái. Không dán
          chung một sổ được.
        */}
        <div className="ml-auto">
          <ExportButton<PiSummaryDto>
            name="proforma-invoices"
            title={t("nav.proformaInvoices")}
            rows={rows}
            total={data?.total}
            errorKey="pi.errorList"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<PiSummaryDto>>("/proforma-invoices", {
                query: { ...filters, q: list.q.trim() || undefined, status, page, pageSize },
                signal,
              })
            }
            columns={[
              { header: t("pi.piNo"), value: (r) => r.piNo },
              { header: t("pi.date"), value: (r) => formatDate(r.date) },
              { header: t("common.customer"), value: (r) => r.billToName },
              { header: t("pi.poNo"), value: (r) => r.customerPoNo },
              // Loại tiền ở cột RIÊNG: ghép vào ô tiền thì ô thành chuỗi và
              // =SUM() trả về 0 — mất đúng lý do người ta xuất ra Excel.
              { header: t("common.currency"), value: (r) => r.currency },
              { header: t("pi.total"), value: (r) => majorUnits(r.totalAmount) },
              { header: t("pi.paid"), value: (r) => majorUnits(r.paid) },
              { header: t("pi.balance"), value: (r) => majorUnits(r.totalAmount - r.paid) },
              { header: t("pi.status"), value: (r) => translateStatus(t, r.paymentStatus) },
            ]}
          />
        </div>
        {user && can("records:create") && (
          <Button
            size="sm"
            onClick={() => navigate(`/proforma-invoices/new${context}`)}
          >
            <Plus className="size-4" />
            {t("pi.newPi")}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchableFilter
          label={t("common.customer")}
          allLabel={t("common.all")}
          searchPlaceholder={t("common.searchPlaceholder")}
          value={filters.customerId ?? ""}
          options={(customers.data?.data ?? []).map((c) => ({
            value: c.id,
            label: c.companyName,
          }))}
          onChange={(v) => list.updateParam("customerId", v || null)}
        />
        <Input
          aria-label={t("pi.respBy")}
          placeholder={t("pi.respBy")}
          className="w-40"
          {...respByInput}
        />
        <Input
          aria-label={t("piWorkspace.from")}
          type="date"
          value={from}
          onChange={(e) => list.updateParam("from", e.target.value || null)}
        />
        <Input
          aria-label={t("piWorkspace.to")}
          type="date"
          value={to}
          onChange={(e) => list.updateParam("to", e.target.value || null)}
        />
        <Select
          aria-label={t("piWorkspace.sort")}
          value={filters.sort ?? "-date"}
          onChange={(e) => list.updateParam("sort", e.target.value)}
        >
          <option value="-date">{t("piWorkspace.newest")}</option>
          <option value="date">{t("piWorkspace.oldest")}</option>
          <option value="piNo">{t("pi.piNo")} ↑</option>
          <option value="-piNo">{t("pi.piNo")} ↓</option>
          <option value="-total">{t("pi.total")} ↓</option>
          <option value="total">{t("pi.total")} ↑</option>
        </Select>
        <Select
          aria-label={t("piWorkspace.columns")}
          value={full ? "full" : "finance"}
          onChange={(e) => list.updateParam("view", e.target.value)}
        >
          <option value="finance">{t("piWorkspace.financeView")}</option>
          <option value="full">{t("piWorkspace.fullView")}</option>
        </Select>
      </div>
      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("pi.errorList")}
        onRetry={() => refetch()}
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("pi.piNo")}</Th>
            <Th>{t("pi.date")}</Th>
            <Th>{t("pi.billToContact")}</Th>
            <Th align="right">{t("pi.total")}</Th>
            <Th align="right">{t("pi.balance")}</Th>
            <Th>{t("pi.status")}</Th>
            {full && (
              <>
                <Th>{t("pi.notifyParty")}</Th>
                <Th>{t("pi.consignee")}</Th>
              </>
            )}
            <Th>{t("pi.poNo")}</Th>
            <Th align="center">{t("common.items")}</Th>
            <Th>{t("common.currency")}</Th>

            <Th>{t("pi.invoiceStatus")}</Th>
            {full && <Th>{t("pi.paymentCompany")}</Th>}
            <Th>{t("pi.respBy")}</Th>
            {full && <Th>{t("common.created")}</Th>}
            <Th align="right" pinRight />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyTableRow colSpan={full?17:13}>{t("common.noMatches")}</EmptyTableRow>
          )}
          {rows.map((p, index) => {
            const balance = p.totalAmount - p.paid;
            return (
              <Tr key={p.id} interactive className="group">
                <Td className="font-mono text-xs text-ink-faint">
                  {(list.page - 1) * list.pageSize + index + 1}
                </Td>
                <Td pin>
                  <Link
                    to={`/proforma-invoices/${p.id}${context}`}
                    className="font-mono tabular font-medium text-brand group-hover:underline"
                  >
                    {p.piNo}
                  </Link>
                  {p.isRed && <Badge tone="danger" outline className="ml-1.5 align-middle text-[10px]">{t("redPi.badge")}</Badge>}
                </Td>
                <Td className="font-mono tabular text-xs text-ink-soft">
                  {formatDate(p.date)}
                </Td>
                <Td>
                  <div className="flex w-64 items-start gap-1.5" title={[p.billToName,p.billToContact].filter(Boolean).join(" · ")}><CompanyTag region={p.billToRegion} className="mt-0.5" /><div className="min-w-0"><Stack top={p.billToName} bottom={p.billToContact} /></div></div>
                </Td>
                <Td align="right" className="font-mono tabular text-ink">
                  {formatMoney(p.totalAmount, p.currency)}
                </Td>
                <Td align="right">
                  <span
                    className={
                      "font-mono tabular " +
                      (balance > 0 ? "font-medium text-ink" : "text-ink-faint")
                    }
                  >
                    {formatMoney(balance, p.currency)}
                  </span>
                </Td>
                <Td>
                  <PaymentStatusChip
                    status={p.paymentStatus}
                    paid={p.paid}
                    total={p.totalAmount}
                  />
                </Td>

                {full && (
                  <>
                    <Td>
                      <Stack
                        top={p.notifyPartyName}
                        bottom={p.notifyPartyContact}
                      />
                    </Td>
                    <Td>
                      <Stack
                        top={p.consigneeName}
                        bottom={p.consigneeContact}
                      />
                    </Td>
                  </>
                )}
                <Td className="font-mono tabular text-xs text-ink-soft">
                  {p.customerPoNo ?? "—"}
                </Td>
                <Td
                  align="center"
                  className="font-mono tabular text-xs text-ink-soft"
                >
                  {p.itemCount}
                </Td>
                <Td className="font-mono tabular text-xs text-ink-soft">
                  {p.currency}
                </Td>
                <Td>
                  {/* Số hoá đơn thật, không chỉ "đã/chưa xuất": nhân viên tra
                      cứu theo số hoá đơn, và huy hiệu có/không bắt họ mở từng
                      PI ra xem. */}
                  <Badge tone={p.hasInvoice ? "brand" : "neutral"}>
                    {p.hasInvoice
                      ? (p.invNo ?? t("pi.invoiced"))
                      : t("pi.notInvoiced")}
                  </Badge>
                </Td>
                {full && (
                  <Td className="text-xs text-ink-soft">
                    {p.paymentCompany ?? "—"}
                  </Td>
                )}
                <Td className="text-xs text-ink-soft">{p.respBy ?? "—"}</Td>
                {full && (
                  <Td>
                    <Stack
                      monoTop
                      top={formatDate(p.createdAt)}
                      bottom={p.createdByName}
                    />
                  </Td>
                )}
                {/* Thao tác lặp hàng ngày: in một xấp PI mà phải mở/đóng từng
                    cái là mất nhiều thời gian hơn cả việc in. */}
                <Td align="right" pinRight>
                  <div className="flex items-center justify-end gap-1">
                    <RowPrintButton
                      path={`/proforma-invoices/${p.id}`}
                      print={printProformaInvoice}
                      label={`${t("common.print")}: ${p.piNo}`}
                    />
                  </div>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
